import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { useRegistrationStore } from '@/lib/store/registrationStore';
import { usePageTranslation } from '@/i18n/useTranslation';
import { authTranslations } from '@/i18n/locales/en/auth';
import { authTranslations as thAuthTranslations } from '@/i18n/locales/th/auth';

interface StepContactProps {
  onNext: () => void;
}

export function StepContact({ onNext }: StepContactProps) {
  const { formData, updateFormData, error, intent } = useRegistrationStore();
  const [localErrors, setLocalErrors] = useState<Record<string, string>>({});
  const t = usePageTranslation({
    en: authTranslations,
    th: thAuthTranslations,
  });

  // Intent-personalized headlines (falls back to generic)
  const intentHeadlines: Record<string, { headline: string; subline: string }> = {
    chat: { headline: t.intentContactHeadlineChat, subline: t.intentContactSublineChat },
    selling: { headline: t.intentContactHeadlineSelling, subline: t.intentContactSublineSelling },
    pos: { headline: t.intentContactHeadlinePos, subline: t.intentContactSublinePos },
    crm: { headline: t.intentContactHeadlineCrm, subline: t.intentContactSublineCrm },
  };
  const headline = (intent && intentHeadlines[intent]?.headline) || t.howCanWeReachYou;

  const validate = () => {
    const errors: Record<string, string> = {};

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      errors.email = t.errorEmailRequired;
    } else if (!emailRegex.test(formData.email)) {
      errors.email = t.errorEmailInvalid;
    }

    // Password validation
    if (!formData.password) {
      errors.password = t.errorPasswordRequired;
    } else if (formData.password.length < 8) {
      errors.password = t.errorPasswordMinLength;
    }

    setLocalErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNext = () => {
    if (validate()) {
      onNext();
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-foreground md:text-center">
          {headline}
        </h2>
      </div>

      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="email">{t.email}</Label>
          <Input
            id="email"
            type="email"
            value={formData.email}
            onChange={(e) => updateFormData({ email: e.target.value })}
            placeholder={t.emailPlaceholder}
            className={`h-12 rounded-md !border-border !bg-background ${localErrors.email ? 'border-destructive' : ''}`}
            autoComplete="email"
            autoFocus
          />
          {localErrors.email && <p className="text-sm text-destructive">{localErrors.email}</p>}
        </div>

        <div className="space-y-2">
          <Label htmlFor="password">{t.password}</Label>
          <Input
            id="password"
            type="password"
            value={formData.password}
            onChange={(e) => updateFormData({ password: e.target.value })}
            placeholder={t.minimumCharacters}
            className={`h-12 rounded-md !border-border !bg-background ${localErrors.password ? 'border-destructive' : ''}`}
            autoComplete="new-password"
          />
          {localErrors.password && <p className="text-sm text-destructive">{localErrors.password}</p>}
        </div>
      </div>

      {error && <p className="text-sm text-destructive text-center">{error}</p>}

      <Button onClick={handleNext} className="w-full h-12 rounded-md text-base">
        {t.next}
      </Button>
    </div>
  );
}
